/**
 * Keeps Step06 candidate versions local until the user explicitly confirms a
 * writeback. The Step03 writeback implementation stays injected so this
 * controller cannot change the Listing contract on its own.
 */
export function createRevisionCandidateController({
  select,
  setState,
  renderCandidatePreviewInto,
  renderExecutionStatus,
  showToast,
  writeRevisionToStep03,
  appendClientTraceCalls,
  renderRevisionFactPanel,
  syncApplyButton,
} = {}) {
  let pending = null;
  let runtimeVersions = [];

  function renderPreview(candidateRecord = null) {
    const preview = select('#revision-candidate-preview');
    const actions = select('#revision-preview-actions');
    const previousButton = select('#previous-revision-candidate');
    const hasCandidatePreview = renderCandidatePreviewInto(preview, candidateRecord);
    if (!actions || !hasCandidatePreview) {
      if (preview) preview.hidden = true;
      if (actions) actions.hidden = true;
      if (previousButton) previousButton.hidden = true;
      return;
    }
    preview.hidden = false;
    actions.hidden = false;
    if (previousButton) previousButton.hidden = runtimeVersions.length <= 1;
  }

  function clear({ clearHistory = false } = {}) {
    pending = null;
    setState('revisionCandidateDraft', null);
    if (clearHistory) {
      runtimeVersions = [];
      setState('revisionCandidateVersions', []);
    }
    renderPreview(null);
  }

  function restore(entry) {
    pending = entry || null;
    const candidateRecord = pending?.candidateRecord || null;
    setState('revisionCandidateDraft', candidateRecord);
    renderPreview(candidateRecord);
  }

  function push(entry) {
    pending = entry;
    runtimeVersions.push(entry);
    setState('revisionCandidateVersions', runtimeVersions.map((item) => item.candidateRecord));
    setState('revisionCandidateDraft', entry.candidateRecord);
  }

  function cancel() {
    if (!pending) return;
    clear({ clearHistory: true });
    const status = select('#revision-execution-status');
    if (status) status.hidden = true;
    showToast('已放弃候选稿，原 Listing 保持不变');
  }

  function previous() {
    if (runtimeVersions.length <= 1) return;
    runtimeVersions.pop();
    pending = runtimeVersions.at(-1) || null;
    const candidateRecord = pending?.candidateRecord || null;
    setState('revisionCandidateVersions', runtimeVersions.map((item) => item.candidateRecord));
    setState('revisionCandidateDraft', candidateRecord);
    renderPreview(candidateRecord);
    if (candidateRecord) {
      renderExecutionStatus({
        tone: 'preview',
        title: `已回到候选稿 v${candidateRecord.version}`,
        message: '可以继续填写改稿要求生成下一版，也可以确认写回 Step03。',
        ...pending.submittedTask,
        functionTrace: pending.functionTrace,
        candidateRecord,
      });
      showToast(`已回到候选稿 v${candidateRecord.version}`);
    }
    syncApplyButton();
  }

  function confirm() {
    if (!pending) return;
    const candidate = pending;
    clear({ clearHistory: true });
    const diffItems = writeRevisionToStep03(
      { ...candidate.normalized, ...candidate.nextDraft },
      candidate.stepReady,
      {
        ...candidate.submittedTask,
        changedFields: candidate.changedFields,
        ...(candidate.revisionCompletion ? { revisionCompletion: candidate.revisionCompletion } : {}),
      }
    );
    appendClientTraceCalls(candidate.functionTrace, [{
      functionName: 'client.confirmPendingRevisionCandidate:output',
      output: { diffItems, targetStep: 3 },
    }]);
    showToast(`已确认并应用 ${diffItems.length} 项变更`);
    renderExecutionStatus({
      tone: 'success',
      title: '改稿已确认并写回 Step03',
      message: `已应用 ${diffItems.length} 项字段变更。`,
      ...candidate.submittedTask,
      functionTrace: candidate.functionTrace,
    });
    setState('revisionMissingFactQuestions', []);
    setState('revisionSupplementalFacts', []);
    renderRevisionFactPanel();
  }

  return {
    getPending: () => pending,
    hasPending: () => Boolean(pending),
    getVersionCount: () => runtimeVersions.length,
    renderPreview,
    clear,
    restore,
    push,
    cancel,
    previous,
    confirm,
  };
}
